import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "../services/api";
import { fetchProducts } from "../services/productService";

/**
 * One shape for a product, whether it came from the catalogue list
 * (GET /business/products) or from a saved reward's `product` block.
 */
export const toPickedProduct = (product) =>
  product
    ? {
        id: product.id,
        name: product.name,
        image: product.image_url ?? product.primary_image?.url ?? null,
        price: product.base_price ?? product.price ?? null,
        isActive: product.is_active !== false,
        // `stock_tracked: false` (no inventory in the plan): sold untracked, so
        // a branch that sells it counts as stocked and shows no count.
        branches: (product.branches ?? []).map((row) => ({
          id: row.branch_id,
          name: row.name,
          tracked: product.stock_tracked !== false,
          stock: Number(row.stock_quantity ?? 0),
          inStock:
            product.stock_tracked === false
              ? row.is_active !== false
              : (row.in_stock ?? (row.is_active !== false && Number(row.stock_quantity ?? 0) > 0)),
        })),
      }
    : null;

function Thumb({ src }) {
  return src ? (
    <img src={src} alt="" className="h-10 w-10 flex-shrink-0 rounded-lg object-cover" />
  ) : (
    <span className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-lg bg-raised text-xs text-ink-2">
      —
    </span>
  );
}

/** "In stock at 2 of 3 branches", or a warning when no branch sells it. */
function StockSummary({ product }) {
  const { t } = useTranslation();

  if (product.branches.length === 0) {
    return (
      <span className="rounded-full bg-warning-wash px-2 py-0.5 text-xs font-medium text-warning-text">
        {t("loyalty.rewards.product.noBranches")}
      </span>
    );
  }

  const stocked = product.branches.filter((b) => b.inStock).length;

  return (
    <span className={`text-xs ${stocked === 0 ? "text-danger-text" : "text-ink-2"}`}>
      {t("loyalty.rewards.product.inStockAt", { count: stocked, total: product.branches.length })}
    </span>
  );
}

/**
 * The free_product reward's product: a searchable list of the business's
 * products (image, name, price, where it is stocked) and, once chosen, a card
 * with its branches and their stock.
 */
export default function RewardProductPicker({ value, onChange, error }) {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [browsing, setBrowsing] = useState(!value);

  const money = (amount) =>
    amount == null
      ? "—"
      : Number(amount).toLocaleString(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Server-side search, a moment after typing stops.
  useEffect(() => {
    if (!browsing) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      setLoading(true);
      setLoadError("");
      fetchProducts({ perPage: 20, filters: { search: search.trim() } })
        .then(({ products }) => !cancelled && setResults((products ?? []).map(toPickedProduct)))
        .catch(
          (err) => !cancelled && setLoadError(apiErrorMessage(err, t("loyalty.rewards.product.loadFailed")))
        )
        .finally(() => !cancelled && setLoading(false));
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, browsing, t]);

  const choose = (product) => {
    onChange(product);
    setBrowsing(false);
  };

  return (
    <fieldset>
      <legend className="field-label">
        {t("loyalty.rewards.product.label")} <span className="text-danger-text">*</span>
      </legend>

      {value && (
        <div className={`rounded-lg border p-3 ${error ? "border-danger" : "border-line"}`}>
          <div className="flex items-center gap-3">
            <Thumb src={value.image} />
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">
                {value.name}
                {!value.isActive && (
                  <span className="ms-2 rounded-full bg-danger-wash px-2 py-0.5 text-xs font-medium text-danger-text">
                    {t("loyalty.rewards.product.inactive")}
                  </span>
                )}
              </p>
              <p className="text-xs text-ink-2" dir="ltr">
                {money(value.price)}
              </p>
            </div>
            {!browsing && (
              <button type="button" className="btn-secondary" onClick={() => setBrowsing(true)}>
                {t("loyalty.rewards.product.change")}
              </button>
            )}
          </div>

          {value.branches.length === 0 ? (
            <p className="mt-2 rounded-lg bg-warning-wash px-3 py-2 text-xs text-warning-text">
              {t("loyalty.rewards.product.noBranchesHint")}
            </p>
          ) : (
            <ul
              className="mt-2 flex flex-wrap gap-1.5"
              aria-label={t("loyalty.rewards.product.stockByBranch")}
            >
              {value.branches.map((b) => (
                <li
                  key={b.id}
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                    b.inStock ? "bg-accent-wash text-accent-text" : "bg-danger-wash text-danger-text"
                  }`}
                >
                  {b.name}
                  {b.tracked && " · "}
                  {b.tracked &&
                    (b.inStock
                      ? t("loyalty.rewards.product.stock", { count: b.stock })
                      : t("loyalty.rewards.product.outOfStock"))}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {browsing && (
        <div className={value ? "mt-2" : ""}>
          <input
            type="search"
            className={`field-input ${error && !value ? "border-danger" : ""}`}
            placeholder={t("loyalty.rewards.product.search")}
            aria-label={t("loyalty.rewards.product.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="mt-1 max-h-60 overflow-y-auto rounded-lg border border-line">
            {loading && results.length === 0 ? (
              <p className="p-3 text-xs text-ink-2">{t("common.loading")}</p>
            ) : loadError ? (
              <p className="field-error p-3">{loadError}</p>
            ) : results.length === 0 ? (
              <p className="p-3 text-xs text-ink-2">{t("loyalty.rewards.product.none")}</p>
            ) : (
              <ul
                className="divide-y divide-line"
                role="listbox"
                aria-label={t("loyalty.rewards.product.label")}
              >
                {results.map((product) => {
                  const selected = value?.id === product.id;
                  return (
                    <li key={product.id} role="option" aria-selected={selected}>
                      <button
                        type="button"
                        className={`flex w-full items-center gap-3 px-3 py-2 text-start hover:bg-raised ${
                          selected ? "bg-accent-wash" : ""
                        }`}
                        onClick={() => choose(product)}
                      >
                        <Thumb src={product.image} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium">{product.name}</span>
                          <StockSummary product={product} />
                        </span>
                        <span className="flex-shrink-0 text-sm text-ink-2" dir="ltr">
                          {money(product.price)}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {value && (
            <button
              type="button"
              className="mt-1 text-xs text-ink-2 underline"
              onClick={() => setBrowsing(false)}
            >
              {t("common.cancel")}
            </button>
          )}
        </div>
      )}

      {error && <p className="field-error">{error}</p>}
    </fieldset>
  );
}
